import React, { useContext } from "react";
import { LanguageContext, UserContext } from "../../../../../Context";
import Lang from "../../../components/Lang";
import { net } from "../../../../../database";
import "./message.css";
import ChatBox from "../../../components/ChatBox";
import Staff from "../../../components/Staff";
import StudentLink from "../../../components/StudentLink";
import HtClass from "../../../components/HtClass";
import FormatDateTime from "../../../components/FormatDateTime";
import Info from "../../../components/Info";
import { Link } from "react-router-dom";
import { Star, MessageCircle, ChevronDown, ChevronUp, CheckCircle } from "lucide-react";

/**
 * Message - Displays a single message or complaint card on the home page.
 * Supports expansion to show chat replies, star-based importance rating,
 * and tagging of students/customers/classes.
 *
 * @author opencode + glm-5.1
 * @date 2026-04-21
 * @version 3.0
 */
export default function Message(props) {
  const [actions] = React.useState(props.message.actions || "");
  const [isExpanded, setIsExpanded] = React.useState(false);
  const { user } = useContext(UserContext);
  const { l, language } = useContext(LanguageContext);
  const {
    _id,
    type,
    studentsTagged,
    customersTagged,
    classesTagged,
    importance,
    description,
    createdAt,
    who,
  } = props.message;

  const finalRecordedBy = props.message.recordedBy || props.message.recordedby;
  let { messages, setMessages, style } = props;

  const closeMessage = () => {
    net.post(`/messages/${_id}/close`, { actions }).then((result) => {
      if (result.sent) {
        props.removeMessage(_id);
      }
    });
  };

  return (
    <div className={`message ${type === "Complaint" ? "is-complaint" : ""}`} style={style}>
      <div className="message-header">
        <div className="header-left">
          <span className="message-type-badge">
            {type === "Complaint" ? <Lang w="complaint" /> : <Lang w="message" />}
          </span>
          <StarRating value={importance} />
        </div>
        <div className="header-right">
          <span className="message-timestamp">
            {new Date(createdAt).toLocaleDateString(language === 0 ? "en-US" : "vi-VN", {
              weekday: "short",
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
            })}
          </span>
          <div className="message-staff">
            <Lang w="recordedBy" /> <Staff id={finalRecordedBy} />
          </div>
        </div>
      </div>

      <div className="message-body">
        <div className="message-main-content">
          {who && who.length > 0 && (
            <div className="message-who">
              <ShowWho who={who} />
            </div>
          )}

          <div className="message-description">{description}</div>

          <div className="message-tags-row">
            <ShowTags tags={customersTagged} tagName={l["customers"][language]} link="customer" />

            {studentsTagged && studentsTagged.length > 0 && (
              <div className="tag-group">
                <span className="tag-label">
                  <Lang w="students" />:
                </span>
                {studentsTagged.map((student, i) => (
                  <div key={student + i} className="tag-pill">
                    <StudentLink student={student} showClasses={false} />
                  </div>
                ))}
              </div>
            )}

            {classesTagged && classesTagged.length > 0 && (
              <div className="tag-group">
                <span className="tag-label">
                  <Lang w="classes" />:
                </span>
                {classesTagged.map((htClass, i) => (
                  <div key={htClass + i} className="tag-pill">
                    <HtClass htClass={htClass} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="message-divider"></div>

        <div className="message-expansion-toggle">
          <button
            className="expansion-btn"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <MessageCircle size={16} />
            <span>
              {props.message.chats?.length > 0
                ? `${isExpanded ? l["hide"][language] : l["view"][language]} ${props.message.chats.length} ${props.message.chats.length === 1 ? l["reply"][language] : l["replies"][language]}`
                : l["addReply"][language]}
            </span>
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>

        {isExpanded && (
          <div className="message-chat-section">
            <ChatBox
              message={props.message}
              user={user}
              setMessages={setMessages}
              messages={messages}
            />
          </div>
        )}

        <div className="message-footer">
          <CloseButton closeMessage={closeMessage} message={props.message} />
        </div>
      </div>
    </div>
  );
}

/**
 * StarRating - Read-only star importance display using lucide Star icons.
 * @author opencode + glm-5.1
 * @date 2026-04-21
 * @version 1.0
 */
function StarRating({ value }) {
  if (!value || value < 1) return null;
  const labels = ["", "Low", "Medium", "High", "Very High", "Urgent"];
  return (
    <div className="star-rating">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          size={14}
          className={`star-icon ${star <= value ? "star-icon--filled" : "star-icon--empty"}`}
        />
      ))}
      {value > 0 && <span className="star-label">{labels[value]}</span>}
    </div>
  );
}

/**
 * CloseButton - Displays resolved info or a "mark as resolved" button.
 * @author opencode + glm-5.1
 * @date 2026-04-21
 * @version 2.0
 */
function CloseButton(props) {
  const { closeMessage, message } = props;

  if (message.status === 1)
    return (
      <div style={{ fontSize: 12, width: 150 }}>
        <Info title={<Lang w="closedBy" />} body={<Staff id={message.closedBy} />} />
        <div>
          <FormatDateTime value={message.closedTimeStamp} />
        </div>
      </div>
    );

  return (
    <button
      className="awesome-close-btn"
      onClick={() => closeMessage()}
      disabled={!message.chats || message.chats.length === 0}
    >
      <CheckCircle size={16} />
      <Lang w="markAsResolved" />
    </button>
  );
}

/**
 * ShowWho - Displays the staff members allocated to this message.
 * @author opencode + glm-5.1
 * @date 2026-04-21
 * @version 2.0
 */
function ShowWho(props) {
  let { who } = props;
  return (
    <React.Fragment>
      <Lang w="allocatedTo" />{" "}
      {who.map((w, i) => (
        <React.Fragment key={w}>
          <Staff id={w} />
          {i < who.length - 1 ? ", " : null}
        </React.Fragment>
      ))}
    </React.Fragment>
  );
}

/**
 * ShowTags - Displays customer tag pills.
 * @author opencode + glm-5.1
 * @date 2026-04-21
 * @version 2.0
 */
function ShowTags(props) {
  let { tags, tagName, link } = props;

  if (!tags || tags.length < 1) return null;
  return (
    <div className="tag-group">
      <span className="tag-label">{tagName}:</span>
      {tags.map((tag) => (
        <Link key={tag._id} className="tag-pill" to={`/${link}/${tag._id}`}>
          {tag.name ? tag.name : tag.phone}
        </Link>
      ))}
    </div>
  );
}
